import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map } from 'rxjs';

import { SegmentTrackEvent } from '@ninety/platform/models/analytics/segment/segment-track-event.enum';
import { selectCurrentUserAsCompanyUser } from '@ninety/platform/state';
import { SegmentActions } from '@ninety/platform/state/app-global/analytics/segment/segment.actions';

import { CascadingMessagesStateActions } from './cascading-messages-state.actions';

/** Non-content fields routed through the generic `update` action; excluded from
 *  cascaded_message_updated (comments are engagement, not an edit, and aren't a tracked event).
 *  An empty change set after exclusion emits nothing — also guards no-op updates. */
const CASCADED_NON_EDIT_FIELDS = ['comments'];

@Injectable()
export class CascadingMessagesAnalyticsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  /** createCascadedForSegment is the dedicated analytics trigger (Universal Create). The legacy
   *  `add` action is never dispatched (no facade.add caller), so it's dropped from the ofType to
   *  avoid a dead/ambiguous second trigger. entityType:'headline' is supplied by the registry. */
  trackCascadedMessageCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.createCascadedForSegment),
      map(({ cascadingMessage }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.CASCADED_CREATED,
          params: {
            entityId: cascadingMessage._id,
            companyId: cascadingMessage.companyId ?? '',
            teamId: cascadingMessage.teamId,
            userId: cascadingMessage.userId,
          },
        })
      )
    )
  );

  /** Cascaded Message Edited — local user's own content edit (update → updateSuccess).
   *  Realtime edits route through updateLocal so this never misattributes; completion has its
   *  own dedicated action. entityType 'headline' (cascaded messages share the headline model;
   *  the event name distinguishes them). */
  trackCascadedMessageUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.updateSuccess),
      map(({ update }) => ({
        update,
        changedFields: Object.keys(update.changes).filter(f => !CASCADED_NON_EDIT_FIELDS.includes(f)),
      })),
      filter(({ changedFields }) => changedFields.length > 0),
      map(({ update, changedFields }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.CASCADED_UPDATED,
          params: {
            entityId: update.id as string,
            changedFields: changedFields.join(','),
          },
        })
      )
    )
  );

  trackCascadedMessageCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.setCompletedSuccess, CascadingMessagesStateActions.markCompletedForSegment),
      concatLatestFrom(() => this.store.select(selectCurrentUserAsCompanyUser)),
      map(([{ update }, currentCompanyUser]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.CASCADED_SET_COMPLETED,
          params: {
            entityId: update.id,
            companyId: currentCompanyUser?.company?._id,
            headlineCompletedFlag: update.changes.isDone,
            headlineCompletedDate: update.changes.completedDate,
          },
        })
      )
    )
  );
}
